/* NIMBUS // Cargo Kit — COMPARE ENGINE (2026-08-07)
 * ─────────────────────────────────────────────────────────────────────────────
 * Wires the [ + COMPARE ] card toggles → pink action bar → [ CARGO COMPARISON ]
 * // SPEC SHOWDOWN overlay. Before this file, compare was markup + CSS only:
 * nimbus-product-card.liquid rendered the button with onclick="return false".
 *
 * ALL CSS ALREADY EXISTED in assets/nimbus-collection.css (.nb-cmpbar, .nb-cmpov,
 * .cmpcols, .cmpduel, .cmp-addpanel …) from the design export. This module renders
 * into those classes and introduces NO new visual patterns — the kit is law, so a
 * new pattern would have to enter nimbus-design-system.md first.
 *
 * DELIBERATELY NOT BUILT — design system §5 records both as tried and removed:
 *   · per-row bar charts   (.cmpbar / .cmpbar-wrap CSS remains, unused)
 *   · the VALUE score row  (.cmp-valuebtn / .cmpcell.value CSS remains, unused)
 * That stale CSS is left in place rather than deleted so this change stays a pure
 * addition; do not re-litigate the rejection by wiring it up.
 *
 * DATA: fetched per product from /products/<handle>?view=cargo-specs
 * (templates/product.cargo-specs.liquid). Nothing about a product is hardcoded here
 * — no key, no label, no unit. Row config lives in snippets/nimbus-spec-fields.liquid (§13).
 *
 * ROWS (2026-10-16 audit): the drawer shows a CURATED list — the category's `card::` rows + `compare::` rows from
 * snippets/nimbus-spec-fields.liquid (payload `cmp`; values read from `rows` by key, fx top/count/usbc, `=price`).
 * Same category → that list. MIXED categories (a GPU + a board) → only the rows every unit shares + a note. Per row:
 * every value equal → all cells DIM (.is-same); values differ → cells LIT (.is-diff); the winner (config direction)
 * gets .best + a ↑ (higher wins) / ↓ (lower wins) marker. Products without a `cmp` list (unconfigured category, stale
 * payload) fall back to the old union of every manifest row. Header (photo, brand / chip line, SKU, price, ✕, ADD
 * TO CARGO) is sticky; on phone 3–4 units scroll two columns at a time (snap), the label column stays put.
 *
 * PERSISTENCE: sessionStorage. The collection grid is destroyed and re-rendered by the
 * Section Rendering API on every facet/pagination change, so in-memory state alone would
 * silently drop the user's pins the moment they filtered. Clears with the tab.
 */
(function () {
  'use strict';

  var KEY = 'nb-compare-v1';
  var MAX = 4;
  var MIN = 2;

  var cache = {};        // handle -> payload (a product's specs are fetched at most once)
  var inflight = {};     // handle -> Promise (dedupes concurrent fetches of the same handle)
  var bar = null;
  var overlay = null;
  var lastFocus = null;

  /* ── state ──────────────────────────────────────────────────────────────── */

  function read() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return [];
      var v = JSON.parse(raw);
      return Array.isArray(v) ? v.filter(function (h) { return typeof h === 'string' && h; }).slice(0, MAX) : [];
    } catch (_) { return []; }
  }

  function write(list) {
    try { sessionStorage.setItem(KEY, JSON.stringify(list)); } catch (_) {}
  }

  function isPinned(handle) { return read().indexOf(handle) > -1; }

  function toggle(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i > -1) { list.splice(i, 1); }
    else {
      // Silently dropping the click at the cap would read as a broken button, so say why.
      if (list.length >= MAX) { flashBar('// BAY FULL · ' + MAX + ' MAX'); return false; }
      list.push(handle);
      prefetch(handle);   // warm the cache while the user is still choosing
    }
    write(list);
    syncButtons();
    renderBar();
    return true;
  }

  function remove(handle) {
    var list = read();
    var i = list.indexOf(handle);
    if (i > -1) { list.splice(i, 1); write(list); }
    syncButtons();
    renderBar();
  }

  function clearAll() { write([]); syncButtons(); renderBar(); }

  /* ── data ───────────────────────────────────────────────────────────────── */

  function fetchSpecs(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '?view=cargo-specs';
    inflight[handle] = fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (json) { cache[handle] = json; delete inflight[handle]; return json; })
      .catch(function (e) { delete inflight[handle]; throw e; });
    return inflight[handle];
  }

  function prefetch(handle) { fetchSpecs(handle).catch(function () {}); }

  /* ── numeric comparison ─────────────────────────────────────────────────── */

  // First number in a spec string: "2,700 MHz" → 2700, "16GB" → 16, "PCIe 5.0 x8" → 5.
  // Only ever called for keys the config marked up/down, so "PCIe 5.0" is never reached.
  function num(v) {
    if (typeof v !== 'string') return null;
    var m = v.replace(/,/g, '').match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  // LARGEST number in the string — config direction `top` (2026-10-15, mobo memory_speed): live values
  // are prose ("DDR5-4400 to 8400 MT/s (OC)"), where the first number is the 5 of DDR5.
  function numTop(v) {
    if (typeof v !== 'string') return null;
    var all = v.replace(/,/g, '').match(/\d+(\.\d+)?/g);
    return all ? Math.max.apply(null, all.map(parseFloat)) : null;
  }

  // Winners are judged per row in judge() (curated rows, 2026-10-16): a tie is not a victory — a row where every unit
  // reads the same value highlights nothing, and only rows with a config direction can win at all.

  /* ── card buttons ───────────────────────────────────────────────────────── */

  function handleOf(btn) {
    var card = btn.closest ? btn.closest('.nb-card') : null;
    return (card && card.dataset && card.dataset.handle) || btn.getAttribute('data-handle') || '';
  }

  // Re-applies pin state to every compare button in the document. Called after the AJAX
  // facet swap and after ORBIT re-mounts, both of which hand us DOM we never touched.
  function syncButtons() {
    var list = read();
    document.querySelectorAll('[data-nimbus-compare]').forEach(function (btn) {
      var h = handleOf(btn);
      var on = h && list.indexOf(h) > -1;
      btn.classList.toggle('on', !!on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var word = btn.querySelector('.nb-cmp-word');
      // Label flips + → − so the toggle's current state is legible without relying on colour
      // alone (retro modes render pink/lime as flat black-and-white).
      if (word) {
        btn.childNodes.forEach(function (n) {
          if (n.nodeType === 3 && /[+−]/.test(n.nodeValue)) n.nodeValue = on ? '− ' : '+ ';
        });
      }
    });
  }

  /* ── action bar ─────────────────────────────────────────────────────────── */

  function buildBar() {
    bar = document.createElement('div');
    bar.className = 'nb-cmpbar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'Comparison bay');
    bar.style.display = 'none';
    bar.innerHTML =
      '<span class="lbl" data-cmp-lbl aria-live="polite"></span>' +
      '<button type="button" class="nb-cmpgo" data-cmp-go></button>' +
      '<button type="button" class="nb-cmpclr" data-cmp-clr>CLEAR</button>';
    document.body.appendChild(bar);
    bar.querySelector('[data-cmp-go]').addEventListener('click', function () {
      if (read().length >= MIN) openOverlay();
    });
    bar.querySelector('[data-cmp-clr]').addEventListener('click', clearAll);
  }

  function renderBar() {
    if (!bar) buildBar();
    var n = read().length;
    if (n === 0) { bar.style.display = 'none'; return; }
    bar.style.display = '';
    // 2026-10-16 (empty states): at 1 unit the reason is VISIBLE text, not only a tooltip (touch has no hover)
    bar.querySelector('[data-cmp-lbl]').textContent = n < MIN ? '// ' + n + ' UNIT PINNED · PICK ' + (MIN - n) + ' MORE TO COMPARE' : '// COMPARISON BAY · ' + n + ' / ' + MAX;
    var go = bar.querySelector('[data-cmp-go]');
    go.textContent = 'COMPARE // ' + n;
    var off = n < MIN;
    go.classList.toggle('is-off', off);
    go.disabled = off;
    // Plain-language reason, per the voice rule: stock/status text never gets cute.
    go.setAttribute('title', off ? 'Select at least ' + MIN + ' units to compare' : 'Open comparison');
  }

  function flashBar(msg) {
    if (!bar) buildBar();
    var lbl = bar.querySelector('[data-cmp-lbl]');
    if (!lbl) return;
    var prev = lbl.textContent;
    lbl.textContent = msg;
    setTimeout(function () { if (lbl.textContent === msg) lbl.textContent = prev; }, 1600);
  }

  /* ── overlay ────────────────────────────────────────────────────────────── */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function openOverlay() {
    // Guard against a double-open: each open registers a capture-phase keydown listener that
    // only closeOverlay() removes, so a second overlay would leave the first listener orphaned
    // and ESC would fire close twice.
    if (overlay) return;
    var handles = read();
    if (handles.length < MIN) return;
    lastFocus = document.activeElement;

    overlay = document.createElement('div');
    overlay.className = 'nb-cmpov';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Cargo comparison');
    overlay.innerHTML =
      '<div class="ovbg" data-cmp-bg></div>' +
      '<div class="cmpmodal" data-cmp-modal>' +
        '<button type="button" class="cmpclose" data-cmp-close aria-label="Close comparison">×</button>' +
        '<div class="cmptag">[ CARGO COMPARISON ]</div>' +
        '<div class="cmpsub">// SPEC SHOWDOWN · LOADING ' + handles.length + ' UNITS</div>' +
        '<div class="cmpbody" data-cmp-body></div>' +
      '</div>';
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';

    overlay.querySelector('[data-cmp-bg]').addEventListener('click', closeOverlay);
    overlay.querySelector('[data-cmp-close]').addEventListener('click', closeOverlay);
    document.addEventListener('keydown', onKey, true);
    overlay.addEventListener('click', onOverlayClick);
    // Narrowing the window can rewrap a button label in one column but not another, which
    // desynchronises the header heights again. Re-square on resize (and on phone rotation).
    window.addEventListener('resize', onResize);

    Promise.all(handles.map(function (h) {
      return fetchSpecs(h).catch(function () { return { ok: false, handle: h }; });
    })).then(function (items) {
      if (!overlay) return;
      renderOverlay(items.filter(function (it) { return it && it.ok; }));
    });
  }

  var resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(equalizeHeads, 120);
  }

  function closeOverlay() {
    if (!overlay) return;
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', onResize);
    clearTimeout(resizeTimer);
    overlay.remove();
    overlay = null;
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function focusables() {
    if (!overlay) return [];
    return Array.prototype.slice.call(overlay.querySelectorAll(
      'button:not([disabled]), a[href], input, select, [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function onKey(e) {
    if (!overlay) return;
    if (e.key === 'Escape') { e.preventDefault(); closeOverlay(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onOverlayClick(e) {
    var t = e.target;
    var rm = t.closest && t.closest('[data-cmp-remove]');
    if (rm) { onRemoveColumn(rm.getAttribute('data-cmp-remove')); return; }
    var add = t.closest && t.closest('[data-cmp-cart]');
    if (add) { addToCargo(add); return; }
    var slot = t.closest && t.closest('[data-cmp-addslot]');
    if (slot) { toggleAddPanel(true); return; }
    var aclose = t.closest && t.closest('[data-cmp-addclose]');
    if (aclose) { toggleAddPanel(false); return; }
    var pick = t.closest && t.closest('[data-cmp-pick]');
    if (pick) { onPick(pick.getAttribute('data-cmp-pick')); return; }
    if (t.closest && t.closest('[data-cmp-back]')) { closeOverlay(); return; }
  }

  function onRemoveColumn(handle) {
    remove(handle);
    var left = read();
    // Nothing left → close. ONE left (2026-10-16 empty states): stay open on the one-unit state (+ ADD UNIT / back),
    // instead of yanking the overlay away mid-task.
    if (!left.length) { closeOverlay(); return; }
    Promise.all(left.map(function (h) {
      return fetchSpecs(h).catch(function () { return { ok: false }; });
    })).then(function (items) {
      if (overlay) renderOverlay(items.filter(function (it) { return it && it.ok; }));
    });
  }

  function onPick(handle) {
    if (!handle) return;
    var list = read();
    if (list.length >= MAX || list.indexOf(handle) > -1) return;
    list.push(handle);
    write(list);
    syncButtons();
    renderBar();
    fetchSpecs(handle).catch(function () { return null; }).then(function () {
      return Promise.all(read().map(function (h) {
        return fetchSpecs(h).catch(function () { return { ok: false }; });
      }));
    }).then(function (items) {
      if (overlay) renderOverlay(items.filter(function (it) { return it && it.ok; }));
    });
  }

  function toggleAddPanel(show) {
    if (!overlay) return;
    var panel = overlay.querySelector('[data-cmp-addpanel]');
    var slot = overlay.querySelector('[data-cmp-addslot]');
    if (panel) panel.style.display = show ? '' : 'none';
    if (slot) slot.style.display = show ? 'none' : '';
    if (show && panel) { var f = panel.querySelector('button'); if (f) f.focus(); }
  }

  /* Candidates for [ + ADD UNIT ] = the products currently in this collection grid.
   * Sourcing from the live DOM (rather than a search endpoint) means the slot respects
   * whatever facets the shopper has already applied — you can only add units that are
   * actually in the manifest you are looking at. */
  function candidates() {
    var picked = read();
    var out = [];
    document.querySelectorAll('.nb-card[data-handle]').forEach(function (card) {
      var h = card.dataset.handle;
      if (!h || picked.indexOf(h) > -1 || out.some(function (c) { return c.handle === h; })) return;
      var t = card.querySelector('[data-shopify-bind="title"]');
      out.push({ handle: h, title: t ? (t.dataset.title || t.textContent.trim()) : h, vendor: (card.dataset.vendor || '') });
    });
    return out;
  }

  /* ── overlay rendering ──────────────────────────────────────────────────── */

  /* HEAD BOX — the column header (image, vendor, title, price, buy button).
   *
   * TWO LAYOUT BUGS FIXED HERE 2026-08-07 after first browser QA, both from the same root
   * cause: `.cmpheadbox` is `min-height` + `justify-content:flex-end`, so a taller title grew
   * the box UPWARD.
   *   ① Titles wrap to different line counts per product, so every column's header was a
   *      different height — which pushed the 40px spec rows out of alignment with each other
   *      and with the label column. A comparison table whose rows don't line up is worse than
   *      no table, because the eye reads across a row that isn't actually one row.
   *   ② The image is absolutely positioned at the top; as the text grew upward it ran straight
   *      underneath the photo, so titles were rendering through the product shot.
   *
   * FIX: reserve a fixed band for the image with padding-top, and pin the title to exactly two
   * lines so it can neither overlap the image nor vary in height. `equalizeHeads()` below then
   * squares up any residual difference, which is what keeps CRT mode honest (--nb-tscale
   * enlarges text there, so hardcoded pixel heights alone would not survive it).
   */
  var IMG_BAND = 132;  // px reserved at the top of the head box: a 110px hero thumb + 22px air (2026-10-16, was 78)

  // "W/H" → W÷H, or 0 when absent / malformed
  function frameN(v) { var m = /^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/.exec(v || ''); return m && +m[2] ? +m[1] / +m[2] : 0; }

  function headBox(it, cls) {
    var img = it.image
      ? (frameN(it.fr)
        // 2026-10-17 IMAGE FRAMES: payload `fr` (snippets/nimbus-frame-ratio.liquid) → the photo box takes the category ratio at
        // the band's height, centred (capped to the column); no `fr` → the old full-width band
        ? '<img src="' + esc(it.image) + '" alt="" loading="lazy" style="position:absolute;top:10px;left:50%;transform:translateX(-50%);height:' + (IMG_BAND - 22) + 'px;width:min(calc(100% - 24px),' + Math.round((IMG_BAND - 22) * frameN(it.fr)) + 'px);object-fit:contain;object-position:center;pointer-events:none;">'
        : '<img src="' + esc(it.image) + '" alt="" loading="lazy" style="position:absolute;top:10px;left:12px;right:12px;height:' + (IMG_BAND - 22) + 'px;width:calc(100% - 24px);object-fit:contain;object-position:center;pointer-events:none;">')
      : '';
    var priceCls = it.available ? 'cmp-price' : 'cmp-price dep';
    var cart = it.available && it.variant_id
      ? '<button type="button" class="cmp-add" data-cmp-cart="' + esc(it.variant_id) + '" data-cmp-title="' + esc(it.title) + '">&gt;&gt; ADD TO CARGO</button>'
      : '<span class="cmp-add off">SOLD OUT</span>';
    // Two-line clamp with an explicit height: a 1-line title still occupies 2 lines, so short
    // and long product names produce identical header heights.
    // 2026-10-15 G4-E card head (payload `tb`, snippets/nimbus-card-title.liquid): brand + family pill in the vendor
    // slot, chip + line in the title slot, one clipped line each. No tiles. null = non-pipeline product → 2-line title.
    var tb = it.tb || null;
    var titleStyle = 'text-decoration:none;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;height:2.5em;';
    return '<div class="' + cls + '" data-cmp-head style="padding-top:' + IMG_BAND + 'px;">' + img +
      '<button type="button" class="cmp-remove" data-cmp-remove="' + esc(it.handle) + '" aria-label="Remove ' + esc(it.title) + ' from comparison">×</button>' +
      (tb ? '<div class="v nb-g4-head">' + ctHead(tb) + '</div>' : '<div class="v">' + esc(it.vendor || '') + '</div>') +
      // SKU under brand + pill, above the chip (2026-10-16 — same order as the card + quick view); full, never ellipsised
      (it.sku ? '<div class="cmp-sku">' + esc(it.sku) + '</div>' : '') +
      (tb
        ? '<a class="t is-ct" href="' + esc(it.url) + '" title="' + esc(it.title) + '" style="text-decoration:none;">' + ctInner(tb) + '</a>'
        : '<a class="t" href="' + esc(it.url) + '" title="' + esc(it.title) + '" style="' + titleStyle + '">' + esc(it.title) + '</a>') +
      '<div class="cmphead-row"><span class="' + priceCls + '">' + esc(it.price) + '</span></div>' +
      cart +
      '</div>';
  }

  // G4-E card head + title — same markup as snippets/nimbus-card-title.liquid ('head' / 'title'); CSS in nimbus-card.css.
  function ctHead(tb) {
    return '<span class="nb-g4-brand">' + esc(tb.brand) + '</span>' +
      (tb.fam ? '<span class="nb-g4-fam is-' + esc(tb.tone || 'acc') + '">' + esc(tb.fam) + '</span>' : '');
  }
  function ctInner(tb) {
    return '<span class="nb-ct-hero">' + esc(tb.hero) + '</span>' +
      (tb.line ? '<span class="nb-ct-line"><span class="nb-ct-txt">' + esc(tb.line) + '</span></span>' : '');
  }

  // Final guard on header alignment: measure every head (including the empty label-column head)
  // and set them all to the tallest. Font-size-agnostic, so it holds in CRT mode where
  // --nb-tscale enlarges everything, and in retro modes that swap the font entirely.
  function equalizeHeads() {
    if (!overlay) return;
    var heads = Array.prototype.slice.call(overlay.querySelectorAll('[data-cmp-head], .cmplabelcol .cmpheadbox'));
    if (heads.length < 2) return;
    heads.forEach(function (h) { h.style.height = 'auto'; });
    var tall = heads.reduce(function (m, h) { return Math.max(m, h.offsetHeight); }, 0);
    heads.forEach(function (h) { h.style.height = tall + 'px'; });
  }

  // Union of every configured row across the selected products, in config order, with
  // rows nobody populated dropped. Keyed by `k` so a mixed selection still aligns.
  function unionRows(items) {
    // Object.create(null), not {} — spec keys come from a config file, and a key named
    // "constructor" or "toString" would otherwise test truthy against Object.prototype
    // and be silently dropped from the table.
    var seen = Object.create(null), order = [];
    items.forEach(function (it) {
      (it.rows || []).forEach(function (r) {
        if (!seen[r.k]) { seen[r.k] = { k: r.k, l: r.l, b: r.b }; order.push(seen[r.k]); }
        // maker-labelled rows (CUDA Cores / Stream Processors / Xe Cores): a mixed selection
        // shows the neutral label `ln` rather than one camp's name over every column
        else if (r.l !== seen[r.k].l) seen[r.k].l = r.ln || r.l;
      });
    });
    return order.filter(function (row) {
      return items.some(function (it) {
        var r = (it.rows || []).filter(function (x) { return x.k === row.k; })[0];
        return r && r.v;
      });
    });
  }

  function valueOf(it, key) {
    if (typeof key === 'object') return cmpValue(it, key);
    var r = (it.rows || []).filter(function (x) { return x.k === key; })[0];
    return (r && r.v) || '';
  }

  /* ── curated rows (2026-10-16) ──────────────────────────────────────────── */

  // card rows first, then compare rows; a compare row with a card row's key replaces it in place. Price always last.
  function cmpList(it) {
    var out = [], at = Object.create(null);
    (it.cmp || []).forEach(function (r) {
      if (at[r.k] !== undefined) { if (r.c) out[at[r.k]] = r; return; }
      at[r.k] = out.length; out.push(r);
    });
    if (out.length && at['=price'] === undefined) out.push({ k: '=price', l: 'PRICE', b: 'down', fx: '', u: '' });
    return out;
  }

  function rawOf(it, k) {
    var r = (it.rows || []).filter(function (x) { return x.k === k; })[0];
    return (r && r.v) || '';
  }

  // USB-C / Type-C ports counted in a prose I/O list: "2x USB-C 20Gbps, 1x USB Type-C" → 3; a bare mention counts 1.
  function usbc(t) {
    if (!t) return '';
    var n = 0, hit = false;
    String(t).split(/[,;\n|·]+/).forEach(function (seg) {
      if (!/(usb[\s-]*c\b|type[\s-]*c\b)/i.test(seg)) return;
      hit = true; var m = seg.match(/(\d+)\s*[x×]/i) || seg.match(/^\s*(\d+)\s/); n += m ? parseInt(m[1], 10) : 1;
    });
    return hit ? String(n) : '0';   // an I/O list with no USB-C = 0 ports
  }

  // 2026-10-16 fx: fan count from a cooling line ("Tri-X, 3 fans" / "Triple Fan" → 3)
  function fans(t) {
    var m = String(t).match(/(\d+)\s*[- ]?fans?\b/i); if (m) return m[1];
    var w = String(t).toLowerCase();
    if (/quad/.test(w)) return '4'; if (/tri/.test(w)) return '3'; if (/dual|twin/.test(w)) return '2'; if (/single|blower/.test(w)) return '1';
    return '';
  }
  // USB ports (all kinds) counted in a prose I/O list
  function usbAll(t) {
    if (!t) return '';
    var n = 0, hit = false;
    String(t).split(/[,;\n|·]+/).forEach(function (seg) {
      if (!/usb/i.test(seg)) return;
      hit = true; var m = seg.match(/(\d+)\s*[x×]/i) || seg.match(/^\s*(\d+)\s/); n += m ? parseInt(m[1], 10) : 1;
    });
    return hit ? String(n) : '0';
  }
  // PCIe x16 slots (count) + the board's PCIe generation: "1x PCIe 5.0 x16, 1x PCIe 4.0 x16" + "5.0" → "2 · PCIe 5.0"
  function pcie(it) {
    var slots = rawOf(it, 'pcie_slots'), gen = rawOf(it, 'pcie_gen'), n = 0, hit = false;
    String(slots).split(/[,;\n|·]+/).forEach(function (seg) {
      if (!/x\s*16\b/i.test(seg) || /\(x\s*[1248]\)/i.test(seg)) return;   // a full-length slot wired x4 isn't an x16
      hit = true; var m = seg.match(/(\d+)\s*[x×]\s*pcie/i); n += m ? parseInt(m[1], 10) : 1;
    });
    if (!hit && !gen) return '';
    var g = String(gen).match(/\d+(\.\d+)?/);
    return (hit ? String(n) : '—') + (g ? ' · PCIe ' + (g[0].indexOf('.') > -1 ? g[0] : g[0] + '.0') : '');
  }

  // row = {k: 'a+b' | 'a>alt' | '=price' | 'key#tag', fx, u}. Returns the display string ('' = no data). `#tag` lets two
  // rows read the same field (rear_io → REAR USB and USB-C REAR) without colliding on the row key.
  function cmpValue(it, row) {
    if (row.k === '=price') return it.price || '';
    if (row.fx === 'pcie') return pcie(it);
    var alts = String(row.k).split('#')[0].split('>'), v = '';
    for (var i = 0; i < alts.length && !v; i++) {
      v = alts[i].split('+').map(function (k) { return rawOf(it, k); }).filter(Boolean).join(' ');
    }
    if (!v) return '';
    if (row.fx === 'top') { var t = numTop(v); return t === null ? v : t + (row.u ? ' ' + row.u : ''); }
    if (row.fx === 'count') { var c = String(v).match(/^\s*(\d+)/); return c ? c[1] : v; }
    if (row.fx === 'usbc') return usbc(v);
    if (row.fx === 'usb') return usbAll(v);
    if (row.fx === 'fans') return fans(v);
    if (row.fx === 'gbps') { var gb = String(v).match(/(\d+(?:\.\d+)?)\s*Gbps/i); return gb ? gb[1] + ' Gbps' : v; }
    return v;
  }

  // numeric read for the winner: price by cents, otherwise the shown string (first number; `top` = largest)
  function cmpNum(it, row, v) {
    if (row.k === '=price') return typeof it.price_cents === 'number' ? it.price_cents : num(v);
    return row.b === 'top' ? numTop(v) : num(v);
  }

  function curatedRows(items) {
    var lists = items.map(cmpList);
    if (lists.some(function (l) { return !l.length; })) return null;            // someone has no curated list → legacy union
    var cats = items.map(function (it) { return it.category || ''; });
    var mixed = cats.some(function (c) { return c !== cats[0]; });
    var rows = lists[0];
    if (mixed) rows = rows.filter(function (r) { return lists.every(function (l) { return l.some(function (x) { return x.k === r.k; }); }); });
    rows = rows.filter(function (r) { return items.some(function (it) { return cmpValue(it, r); }); });
    return { rows: rows, mixed: mixed };
  }

  // per-row verdict: values + winners + same/diff
  function judge(items, row) {
    var vals = items.map(function (it) { return cmpValue(it, row); });
    var norm = vals.map(function (v) { return String(v).toLowerCase().replace(/\s+/g, ' ').trim(); });
    var same = norm.every(function (v) { return v && v === norm[0]; });
    var win = null;
    if (row.b && !same) {
      var nums = items.map(function (it, i) { return vals[i] ? cmpNum(it, row, vals[i]) : null; });
      var present = nums.filter(function (n) { return n !== null && !isNaN(n); });
      if (present.length >= 2) {
        var best = row.b === 'down' ? Math.min.apply(null, present) : Math.max.apply(null, present);
        if (!present.every(function (n) { return n === best; })) { win = {}; nums.forEach(function (n, i) { if (n === best) win[i] = true; }); }
      }
    }
    return { vals: vals, same: same, win: win };
  }
  function mark(row) { return row.b === 'down' ? '↓' : '↑'; }
  function cellCls(j, i) { return !j.vals[i] ? ' is-none' : j.same ? ' is-same' : ' is-diff'; }

  function renderOverlay(items) {
    if (!overlay) return;
    var body = overlay.querySelector('[data-cmp-body]');
    var sub = overlay.querySelector('.cmpsub');

    if (items.length < MIN) {
      // EMPTY STATES (2026-10-16): the kit .nb-empty pane (assets/nimbus-mode.css) — no brackets on its controls.
      if (sub) sub.textContent = '// SPEC SHOWDOWN';
      var one = items.length === 1 && read().length === 1, c1 = candidates();
      var nm = one ? ((items[0].tb && items[0].tb.hero ? items[0].tb.hero + (items[0].tb.line ? ' ' + items[0].tb.line : '') : items[0].title) || 'This unit') : '';
      var h = '<div class="nb-empty nb-empty--inline" role="status"><div class="nb-empty-hazard" aria-hidden="true"></div>' +
        '<p class="nb-empty-eyebrow">// COMPARISON BAY · ' + read().length + ' / ' + MAX + '</p>' +
        (one
          ? '<p class="nb-empty-title">ONE UNIT IN THE BAY</p><p class="nb-empty-body">' + esc(nm) + ' is pinned. Add one more unit to see the spec showdown side by side.</p>'
          : '<p class="nb-empty-title">SPEC DATA UNAVAILABLE</p><p class="nb-empty-body">These units have no spec manifest published yet, so there is nothing to line up. Try units from the same category.</p>') +
        '<div class="nb-empty-actions">' +
        (one && c1.length ? '<button type="button" class="nb-empty-btn is-primary" data-cmp-addslot>+ ADD UNIT</button>' : '') +
        '<button type="button" class="nb-empty-btn" data-cmp-back>BACK TO THE GRID</button></div></div>';
      if (one && c1.length) {
        h += '<div class="cmp-addpanel" data-cmp-addpanel style="display:none;"><div class="cmp-addpanel-head"><span>[ ADD UNIT ]</span>' +
          '<button type="button" class="cmp-addclose" data-cmp-addclose aria-label="Close add unit">×</button></div><div class="cmp-addlist">' +
          c1.map(function (c) { return '<button type="button" class="cmp-additem" data-cmp-pick="' + esc(c.handle) + '"><span class="au-v">' + esc(c.vendor) + '</span><span class="au-n">' + esc(c.title) + '</span></button>'; }).join('') +
          '</div></div>';
      }
      body.innerHTML = h;
      var fb = body.querySelector('.nb-empty-btn'); if (fb) fb.focus();
      return;
    }
    var cur = curatedRows(items);
    var rows = cur ? cur.rows : unionRows(items);
    var mixed = !!(cur && cur.mixed);
    if (sub) sub.textContent = '// SPEC SHOWDOWN · ' + items.length + ' UNITS' + (mixed ? ' · DIFFERENT CATEGORIES' : '');
    var html = mixed ? '<div class="cmp-note" role="note">// DIFFERENT CATEGORIES — SHOWING SHARED ROWS ONLY</div>' : '';
    html += rows.length
      ? (items.length === 2 ? duelHtml(items, rows) : tableHtml(items, rows))
      : '<div class="cmpsub" style="margin-top:22px;">// NO SPEC MANIFEST PUBLISHED FOR THESE UNITS</div>';

    var cands = candidates();
    if (items.length < MAX && cands.length) {
      html += '<button type="button" class="cmp-addslot" data-cmp-addslot>+ ADD UNIT</button>';
      html += '<div class="cmp-addpanel" data-cmp-addpanel style="display:none;">' +
        '<div class="cmp-addpanel-head"><span>[ ADD UNIT ]</span>' +
        '<button type="button" class="cmp-addclose" data-cmp-addclose aria-label="Close add unit">×</button></div>' +
        '<div class="cmp-addlist">' +
        cands.map(function (c) {
          return '<button type="button" class="cmp-additem" data-cmp-pick="' + esc(c.handle) + '">' +
            '<span class="au-v">' + esc(c.vendor) + '</span>' +
            '<span class="au-n">' + esc(c.title) + '</span></button>';
        }).join('') +
        '</div></div>';
    }

    body.innerHTML = html;
    equalizeHeads();
    // Product images load async and can change a head's height after first measure, so square up
    // again once each one lands. `once` so a 4-column table costs at most 4 extra passes.
    overlay.querySelectorAll('[data-cmp-head] img').forEach(function (im) {
      if (!im.complete) im.addEventListener('load', equalizeHeads, { once: true });
    });
    var first = overlay.querySelector('.cmpclose');
    if (first) first.focus();
  }

  // 3–4 units: multi-column table, label column on the left.
  function tableHtml(items, rows) {
    var cols = '<div class="cmpcol cmplabelcol">' +
      '<div class="cmpheadbox" aria-hidden="true"></div>' +
      rows.map(function (r) { return '<div class="cmprowlabel">' + esc(r.l) + (r.b ? '<span class="cmp-dir" aria-hidden="true">' + mark(r) + '</span>' : '') + '</div>'; }).join('') +
      '</div>';

    var cells = items.map(function () { return []; });
    rows.forEach(function (r) {
      var j = judge(items, r);
      j.vals.forEach(function (v, i) {
        var best = j.win && j.win[i];
        cells[i].push('<div class="cmpcell' + cellCls(j, i) + (best ? ' best' : '') + '" title="' + esc(v || '—') + '"><span class="cmpv">' + esc(v || '—') + '</span>' +
          (best ? '<span class="cmp-mk" aria-label="best">' + mark(r) + '</span>' : '') + '</div>');
      });
    });

    cols += items.map(function (it, i) {
      return '<div class="cmpcol">' + headBox(it, 'cmpheadbox') + cells[i].join('') + '</div>';
    }).join('');

    return '<div class="cmpcols">' + cols + '</div>';
  }

  // Exactly 2 units: head-to-head. Mirrored rows either side of a central label lane,
  // winning side flashes green (.duel-cell.win).
  function duelHtml(items, rows) {
    var heads = '<div class="duel-heads">' +
      headBox(items[0], 'duel-head') +
      '<div class="duel-vs" aria-hidden="true">VS</div>' +
      headBox(items[1], 'duel-head') +
      '</div>';

    var body = rows.map(function (r) {
      var j = judge(items, r), a = j.vals[0], b = j.vals[1], win = j.win;
      var mk = '<span class="cmp-mk" aria-label="best">' + mark(r) + '</span>';
      return '<div class="duel-row">' +
        '<div class="duel-cell l' + cellCls(j, 0) + (win && win[0] ? ' win' : '') + '">' + (win && win[0] ? mk : '') + '<span class="cmpv">' + esc(a || '—') + '</span></div>' +
        '<div class="duel-label">' + esc(r.l) + (r.b ? '<span class="cmp-dir" aria-hidden="true">' + mark(r) + '</span>' : '') + '</div>' +
        '<div class="duel-cell r' + cellCls(j, 1) + (win && win[1] ? ' win' : '') + '"><span class="cmpv">' + esc(b || '—') + '</span>' + (win && win[1] ? mk : '') + '</div>' +
        '</div>';
    }).join('');

    return '<div class="cmpduel">' + heads + body + '</div>';
  }

  /* ── add to cargo ───────────────────────────────────────────────────────── */

  function addToCargo(btn) {
    var id = btn.getAttribute('data-cmp-cart');
    if (!id || btn.disabled) return;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = '… ADDING';

    var drawer = document.querySelector('cart-drawer');
    var payload = { id: Number(id), quantity: 1 };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); }).then(function (json) {
      if (json && json.status) throw new Error(json.description || json.message || 'add failed');
      btn.textContent = '✓ IN CARGO';
      // Hand the fresh sections to AREA's drawer so the bubble + drawer stay truthful.
      // The drawer opens over this overlay, so close ours — two stacked modals would
      // trap focus in the wrong one.
      if (drawer && json && json.sections) {
        closeOverlay();
        drawer.renderContents(json);
      } else {
        setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 1400);
      }
    }).catch(function () {
      btn.textContent = '✕ COULD NOT ADD';
      setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 1800);
    });
  }

  /* ── boot ───────────────────────────────────────────────────────────────── */

  // Delegated at document level: card buttons are destroyed and rebuilt by every AJAX
  // facet swap, and shuttled into .orbit-card wrappers by ORBIT. A listener bound to a
  // card would not survive either.
  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-nimbus-compare]');
      if (!btn) return;
      e.preventDefault();
      var h = handleOf(btn);
      if (h) toggle(h);
    });
  }

  var API = {
    sync: function () { syncButtons(); renderBar(); },
    // 2026-10-16: the quick view's + COMPARE pill pins / unpins through the same state as the card toggles
    toggle: function (handle) { return handle ? toggle(handle) : false; },
    has: isPinned,
    open: openOverlay,
    clear: clearAll,
    selected: read
  };

  function boot() { bind(); syncButtons(); renderBar(); }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  window.NimbusCompare = API;
})();
